/// <amd-module name="VSS/Features/Charts/ChartLegendSizeService" />
import { IVssService, Services, VssService } from "VSS/Platform/Context";

/// <summary>Provides limit heuristics for predictably rendering a large number of series on a chart.</summary>
export interface IChartLegendSizeService extends IVssService {
    getDefaultSeriesLimit(): number;
    getDefaultGroupByLimit(): number;
    getDefaultLegendHeight(): number;
    getHorizontalLegendElements(width: number, rowCount?: number): number;
    getVerticalLegendElements(height: number): number;
    getAllowedRows(height: number, reservedVerticalSpace?: number): number;
    getAllowedColumns(width: number, reservedHorizontalSpace?: number): number;
    getReservedVerticalSpaceForRows(): number;
    getReservedHorizontalSpaceForCols(): number;
    getReservedHorzontalSpaceForLegend(): number;
    getReservedVerticalSpaceForLegend(): number;
    getWidthPerLegendItem(): number;
}

export class ChartLegendSizeService extends VssService implements IChartLegendSizeService {
    public getDefaultSeriesLimit(): number {
        return 1;
    }

    public getDefaultGroupByLimit(): number {
        return 1;
    }

    public getDefaultHorizontalLegendRowCount(): number {
        return 3;
    }

    public getWidthPerLegendItem(): number {
        return 100;
    }

    public getHeightPerLegendItem(): number {
        return 16;
    }

    public getHeightRowItem(): number {
        return 20;
    }

    public getWidthPerColumnItem(): number {
        return 40;
    }

    // Number of cells which are reserved for "other" group in peak element scenarios
    public getOtherItemReservation(): number {
        return 1;
    }

    // Standard "dead" space which is not available for rendering
    public getReservedHorizontalSpaceForCols(): number {
        return 50;
    }

    public getReservedVerticalSpaceForRows(): number {
        return 40;
    }

    public getReservedVerticalSpaceForLegend(): number {
        return 36;
    }

    public getDefaultLegendHeight(): number {
        return this.getDefaultHorizontalLegendRowCount() * this.getHeightRowItem();
    }

    public getReservedHorzontalSpaceForLegend(): number {
        return 160;
    }

    /**
     * Determines the allowable number of elements for presentation in a horizontal legend.
     * @param width
     * @param rowCount
     */
    public getHorizontalLegendElements(width: number, rowCount: number = this.getDefaultHorizontalLegendRowCount()): number {
        return Math.max(0, Math.floor(width / this.getWidthPerLegendItem()) * rowCount - this.getOtherItemReservation());
    }

    /**
     * Determines the allowable number of elements for presentation in a vertical legend.
     * @param height
     */
    public getVerticalLegendElements(height: number): number {
        let availableWidth = Math.max(0, height - this.getReservedVerticalSpaceForLegend());
        return Math.max(0, Math.floor(availableWidth / this.getHeightPerLegendItem()) - this.getOtherItemReservation());
    }

    /**
     * Determines the allowed number of rows for views such as pivot table and bar chart
     * @param height
     */
    public getAllowedRows(height: number, reservedVerticalSpace: number = this.getReservedVerticalSpaceForRows()): number {
        let availableWidth = Math.max(0, height - reservedVerticalSpace);
        return Math.max(0, Math.floor(availableWidth / this.getHeightRowItem()) - this.getOtherItemReservation());
    }

    /**
     * Determines the allowed number of columns for views such as pivot table and column chart
     * @param height
     */
    public getAllowedColumns(width: number, reservedHorizontalSpace: number = this.getReservedHorizontalSpaceForCols()): number {
        let availableHeight = Math.max(0, width - reservedHorizontalSpace);
        return Math.max(0, Math.floor(availableHeight / this.getWidthPerColumnItem()) - this.getOtherItemReservation());
    }
}

Services.add("IChartLegendSizeService", { serviceFactory: ChartLegendSizeService });
